import React, { useState } from "react";
import "../inward-styles.css";
import Icon from "@mdi/react";
import { mdiAlertCircleOutline } from "@mdi/js";
import { MobileCard, MobileField } from "../common-components/MobileCard";
import DrawerModal, {
  DrawerModalLoaction,
} from "../../../new_components/common/drawer_modal";
import { urls } from "../../../../utils/urls";

const formatMoney = (value: unknown) =>
  typeof value === "number"
    ? `₹${value.toLocaleString("en-IN", { maximumFractionDigits: 2 })}`
    : "--";

type VoucherRef = {
  type: "PO" | "ORDER" | "OTHER";
  label: string;
  link_id?: string | null;
};

const cleanRefs = (refs?: VoucherRef[]): VoucherRef[] =>
  Array.isArray(refs) ? refs.filter((r) => r && r.label) : [];

// Only PO and ORDER have destination pages. PO routes by internal id (resolved
// server-side into link_id); ORDER routes by the order id itself. Everything
// else (ST / Restore / inward orders) has no page → rendered as plain text.
const refHref = (ref: VoucherRef): string | null => {
  if (!ref?.link_id) return null;
  if (ref.type === "PO")
    return `${urls.purchase_order}/view-detail/${ref.link_id}`;
  if (ref.type === "ORDER") return `/orders/${ref.link_id}`;
  return null;
};

const InventoryStockList = ({
  data,
  isMovementView = false,
}: {
  data: any[];
  isMovementView?: boolean;
}) => {
  const [drawer, setDrawer] = useState<{
    open: boolean;
    title: string;
    refs: VoucherRef[];
  }>({ open: false, title: "", refs: [] });

  const openVouchers = (title: string, refs?: VoucherRef[]) =>
    setDrawer({ open: true, title, refs: cleanRefs(refs) });

  // Qty + a button that opens the reference list in a right-side drawer.
  const MovementTotal = ({
    qty,
    refs,
    title,
    tone,
  }: {
    qty: number;
    refs?: VoucherRef[];
    title: string;
    tone: string;
  }) => {
    const count = cleanRefs(refs).length;
    return (
      <div className="flex flex-col items-center gap-1">
        <span className={`font-bold ${tone}`}>{qty ?? 0}</span>
        {count > 0 && (
          <button
            id={`warehouse-inventory-view-vouchers-btn-${title}`}
            onClick={() => openVouchers(title, refs)}
            className="text-blue-600 hover:underline text-[11px] font-semibold whitespace-nowrap"
          >
            View vouchers ({count})
          </button>
        )}
      </div>
    );
  };

  return (
    <div className="rounded-lg border shadow-sm bg-white">
      <div className="p-6 pb-3">
        <h3 className="text-base font-bold">Stock Summary</h3>
      </div>

      <div className="p-6 pt-0 overflow-x-auto relative hidden md:block">
        <table className="w-full min-w-max text-sm gray">
          <thead className="border-b sticky top-0 bg-white z-10">
            <tr>
              <th className="p-3 pr-5 text-left text-muted-foreground whitespace-nowrap sticky left-0 bg-white z-20">
                Item Code
              </th>
              <th className="p-3 text-left text-muted-foreground w-80">
                Item Name
              </th>
              <th className="p-3 text-left text-muted-foreground whitespace-nowrap">
                Item Type
              </th>
              <th className="p-3 text-left text-muted-foreground w-60">
                Warehouse Name
              </th>

              {isMovementView ? (
                <>
                  <th className="p-3 text-center text-muted-foreground whitespace-nowrap">
                    Opening Stock
                  </th>
                  <th className="p-3 text-right text-muted-foreground whitespace-nowrap">
                    Opening WAC
                  </th>
                  <th className="p-3 text-right text-muted-foreground whitespace-nowrap">
                    Opening Stock Value
                  </th>
                  <th className="p-3 text-center text-muted-foreground whitespace-nowrap">
                    Closing Stock
                  </th>
                  <th className="p-3 text-right text-muted-foreground whitespace-nowrap">
                    Closing WAC
                  </th>
                  <th className="p-3 text-right text-muted-foreground whitespace-nowrap">
                    Closing Stock Value
                  </th>
                  <th className="p-3 text-center text-muted-foreground whitespace-nowrap">
                    Total Inwards
                  </th>
                  <th className="p-3 text-center text-muted-foreground whitespace-nowrap">
                    Total Outwards
                  </th>
                </>
              ) : (
                <>
                  <th className="p-3 text-center text-muted-foreground whitespace-nowrap">
                    Stock
                  </th>
                  <th className="p-3 text-center text-muted-foreground whitespace-nowrap">
                    Pending Outwards
                  </th>
                  <th className="p-3 text-center text-muted-foreground whitespace-nowrap">
                    In Transit
                  </th>
                  <th className="p-3 text-right text-muted-foreground whitespace-nowrap">
                    WAC
                  </th>
                  <th className="p-3 text-right text-muted-foreground whitespace-nowrap">
                    Stock Value
                  </th>
                </>
              )}

              <th className="p-3 text-center text-muted-foreground whitespace-nowrap">
                Faulty Devices
              </th>
            </tr>
          </thead>

          <tbody>
            {data.map((row, i) => (
              <tr
                key={i}
                id={`warehouse-inventory-stock-row-${row?.item_code}-${i}`}
                className="border-b hover:bg-gray-50"
              >
                <td className="p-3 pr-5 font-bold whitespace-nowrap sticky left-0 bg-white z-10">
                  {row?.item_code}
                </td>

                <td className="p-3 font-medium w-80 break-words whitespace-normal">
                  <div className="flex flex-col gap-0.5">
                    <div>{row?.item_name}</div>
                    <div className="text-muted-foreground w-80 break-words whitespace-normal">
                      {row?.item_description || "--"}
                    </div>
                  </div>
                </td>
                <td className="p-3 text-left font-medium whitespace-nowrap">
                  {row?.item_subcategory === "Finished Goods"
                    ? "Finished Goods"
                    : "Raw Material"}
                </td>
                <td className="p-3 text-left font-bold w-60 break-words whitespace-normal">
                  {row?.warehouse_name}
                </td>

                {isMovementView ? (
                  <>
                    <td className="p-3 text-center font-bold whitespace-nowrap">
                      {row?.opening_stock ?? 0}
                    </td>
                    <td className="p-3 text-right font-bold whitespace-nowrap">
                      {formatMoney(row?.opening_wac)}
                    </td>
                    <td className="p-3 text-right font-bold text-success whitespace-nowrap">
                      {formatMoney(row?.opening_stock_value)}
                    </td>
                    <td className="p-3 text-center text-success font-bold whitespace-nowrap">
                      {row?.closing_stock ?? row?.closing_stock_qty ?? 0}
                    </td>
                    <td className="p-3 text-right font-bold whitespace-nowrap">
                      {formatMoney(row?.closing_wac)}
                    </td>
                    <td className="p-3 text-right font-bold text-success whitespace-nowrap">
                      {formatMoney(row?.closing_stock_value)}
                    </td>
                    <td className="p-3 align-top">
                      <MovementTotal
                        qty={row?.total_inwards ?? 0}
                        refs={row?.inward_refs}
                        tone="text-success"
                        title={`Inward Vouchers — ${row?.item_code ?? ""}`}
                      />
                    </td>
                    <td className="p-3 align-top">
                      <MovementTotal
                        qty={row?.total_outwards ?? 0}
                        refs={row?.outward_refs}
                        tone="text-warning"
                        title={`Outward Vouchers — ${row?.item_code ?? ""}`}
                      />
                    </td>
                  </>
                ) : (
                  <>
                    <td className="p-3 text-center text-success font-bold whitespace-nowrap">
                      <span className="inline-flex items-center gap-1 justify-center">
                        {Math.max(row?.closing_stock_qty ?? 0, 0)}
                        {row?.is_over_allocated && (
                          <Icon
                            path={mdiAlertCircleOutline}
                            size={0.7}
                            color="#dc2626"
                            title={`Over-allocated: raw balance is ${row?.closing_stock_qty}`}
                          />
                        )}
                      </span>
                    </td>
                    <td className="p-3 text-right text-warning font-bold whitespace-nowrap">
                      {row?.pending_outwards ?? 0}
                    </td>
                    <td className="p-3 text-right text-primary font-bold whitespace-nowrap">
                      {row?.in_transit ?? 0}
                    </td>
                    <td className="p-3 text-right font-bold whitespace-nowrap">
                      {formatMoney(row?.wac_value)}
                    </td>
                    <td className="p-3 text-right font-bold text-success whitespace-nowrap">
                      {formatMoney(row?.stock_value)}
                    </td>
                  </>
                )}

                <td className="p-3 text-center text-red-600 font-bold whitespace-nowrap">
                  {row?.faulty_stock_qty ?? 0}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="md:hidden p-4 pt-0 space-y-3">
        {data.map((row, i) => (
          <MobileCard
            key={i}
            id={`warehouse-inventory-stock-card-${row?.item_code}-${i}`}
          >
            <div className="flex items-start justify-between gap-2 mb-2">
              <div className="min-w-0">
                <p className="text-xs font-bold text-gray-900 break-words">
                  {row?.item_code}
                </p>
                <p className="text-xs text-gray-600 break-words">
                  {row?.item_name}
                </p>
                {row?.item_description && (
                  <p className="text-[11px] text-gray-500 break-words">
                    {row?.item_description}
                  </p>
                )}
              </div>
              <span className="inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium bg-gray-100 text-gray-700 shrink-0">
                {row?.item_subcategory === "Finished Goods"
                  ? "Finished Goods"
                  : "Raw Material"}
              </span>
            </div>
            <div className="divide-y divide-gray-100">
              <MobileField label="Warehouse">{row?.warehouse_name}</MobileField>

              {isMovementView ? (
                <>
                  <MobileField label="Opening Stock">
                    <span className="font-bold">{row?.opening_stock ?? 0}</span>
                  </MobileField>
                  <MobileField label="Opening WAC">
                    <span className="font-bold">
                      {formatMoney(row?.opening_wac)}
                    </span>
                  </MobileField>
                  <MobileField label="Opening Stock Value">
                    <span className="text-success font-bold">
                      {formatMoney(row?.opening_stock_value)}
                    </span>
                  </MobileField>
                  <MobileField label="Closing Stock">
                    <span className="text-success font-bold">
                      {row?.closing_stock ?? row?.closing_stock_qty ?? 0}
                    </span>
                  </MobileField>
                  <MobileField label="Closing WAC">
                    <span className="font-bold">
                      {formatMoney(row?.closing_wac)}
                    </span>
                  </MobileField>
                  <MobileField label="Closing Stock Value">
                    <span className="text-success font-bold">
                      {formatMoney(row?.closing_stock_value)}
                    </span>
                  </MobileField>
                  <MobileField label="Total Inwards">
                    <div className="flex flex-col items-end gap-1">
                      <span className="text-success font-bold">
                        {row?.total_inwards ?? 0}
                      </span>
                      {cleanRefs(row?.inward_refs).length > 0 && (
                        <button
                          id={`warehouse-inventory-mobile-view-vouchers-inward-btn-${i}`}
                          onClick={() =>
                            openVouchers(
                              `Inward Vouchers — ${row?.item_code ?? ""}`,
                              row?.inward_refs,
                            )
                          }
                          className="text-blue-600 hover:underline text-[11px] font-semibold"
                        >
                          View vouchers ({cleanRefs(row?.inward_refs).length})
                        </button>
                      )}
                    </div>
                  </MobileField>
                  <MobileField label="Total Outwards">
                    <div className="flex flex-col items-end gap-1">
                      <span className="text-warning font-bold">
                        {row?.total_outwards ?? 0}
                      </span>
                      {cleanRefs(row?.outward_refs).length > 0 && (
                        <button
                          id={`warehouse-inventory-mobile-view-vouchers-outward-btn-${i}`}
                          onClick={() =>
                            openVouchers(
                              `Outward Vouchers — ${row?.item_code ?? ""}`,
                              row?.outward_refs,
                            )
                          }
                          className="text-blue-600 hover:underline text-[11px] font-semibold"
                        >
                          View vouchers ({cleanRefs(row?.outward_refs).length})
                        </button>
                      )}
                    </div>
                  </MobileField>
                </>
              ) : (
                <>
                  <MobileField label="Stock">
                    <span className="text-success font-bold inline-flex items-center gap-1">
                      {Math.max(row?.closing_stock_qty ?? 0, 0)}
                      {row?.is_over_allocated && (
                        <Icon
                          path={mdiAlertCircleOutline}
                          size={0.7}
                          color="#dc2626"
                          title={`Over-allocated: raw balance is ${row?.closing_stock_qty}`}
                        />
                      )}
                    </span>
                  </MobileField>
                  <MobileField label="Pending Outwards">
                    <span className="text-warning font-bold">
                      {row?.pending_outwards ?? 0}
                    </span>
                  </MobileField>
                  <MobileField label="In Transit">
                    <span className="text-primary font-bold">
                      {row?.in_transit ?? 0}
                    </span>
                  </MobileField>
                  <MobileField label="WAC">
                    <span className="font-bold">
                      {formatMoney(row?.wac_value)}
                    </span>
                  </MobileField>
                  <MobileField label="Stock Value">
                    <span className="text-success font-bold">
                      {formatMoney(row?.stock_value)}
                    </span>
                  </MobileField>
                </>
              )}

              <MobileField label="Faulty Devices">
                <span className="text-red-600 font-bold">
                  {row?.faulty_stock_qty ?? 0}
                </span>
              </MobileField>
            </div>
          </MobileCard>
        ))}
      </div>

      {drawer.open && (
        <DrawerModal
          show={drawer.open}
          title={drawer.title}
          location={DrawerModalLoaction.right}
          onClose={() => setDrawer((d) => ({ ...d, open: false }))}
        >
          <div className="p-4">
            <p className="text-xs text-muted-foreground mb-3">
              {drawer.refs.length} voucher{drawer.refs.length === 1 ? "" : "s"}
            </p>
            <div className="flex flex-col gap-2">
              {drawer.refs.map((ref, i) => {
                const href = refHref(ref);
                return (
                  <div
                    key={i}
                    className="rounded-md border px-3 py-2 text-sm font-medium break-all flex items-center justify-between gap-2"
                  >
                    {href ? (
                      <a
                        id={`warehouse-inventory-voucher-link-${i}`}
                        href={href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline"
                      >
                        {ref.label}
                      </a>
                    ) : (
                      <span>{ref.label}</span>
                    )}
                    {ref.type !== "OTHER" && (
                      <span className="text-[10px] uppercase text-muted-foreground shrink-0">
                        {ref.type}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </DrawerModal>
      )}
    </div>
  );
};

export default InventoryStockList;
